import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import lottie, { AnimationItem } from 'lottie-web'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { cn } from '../../utils/cn'

interface Props {
  isCompleted: boolean
  onAnimationComplete?: (forceDismiss?: boolean) => void
  stopAnimation?: boolean
  minVisibleMs?: number
}

const FullScreenLoading = ({
  isCompleted,
  onAnimationComplete,
  stopAnimation,
  minVisibleMs = 7200,
}: Props) => {
  const [isLoadingMessageVisible, setIsLoadingMessageVisible] = useState(false)
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false)
  const [canDismiss, setCanDismiss] = useState(false)
  const [isForceDismiss, setIsForceDismiss] = useState(false)
  const animationRef = useRef<AnimationItem | null>(null)
  const loadSafetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const stopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedAtRef = useRef<number>(0)
  const stopHandledRef = useRef(false)
  const [isAnimationReady, setIsAnimationReady] = useState(false)
  const { t } = useTranslation()

  const finish = (forceDismiss = false) => {
    setIsForceDismiss(forceDismiss)
    setCanDismiss(true)
  }

  const clearAllTimers = () => {
    if (loadSafetyTimeoutRef.current) clearTimeout(loadSafetyTimeoutRef.current)
    if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current)
    loadSafetyTimeoutRef.current = null
    stopTimeoutRef.current = null
  }

  useLayoutEffect(() => {
    mountedAtRef.current = Date.now()
    stopHandledRef.current = false

    const element = document.getElementById('lottie')
    if (!element) {
      finish(true)
      return
    }

    const animation = lottie.loadAnimation({
      container: element,
      renderer: 'svg',
      loop: false,
      autoplay: true,
      path: '/animation/motivum_loading.json',
    })

    animationRef.current = animation
    setIsAnimationReady(true)
    clearAllTimers()

    loadSafetyTimeoutRef.current = setTimeout(() => finish(false), 15000)

    const handleComplete = () => {
      clearAllTimers()
      setHasPlayedOnce(true)
    }

    const handleError = () => {
      clearAllTimers()
      finish(true)
    }

    animation.addEventListener('complete', handleComplete)
    animation.addEventListener('data_failed', handleError)

    return () => {
      clearAllTimers()
      setIsAnimationReady(false)
      animation.removeEventListener('complete', handleComplete)
      animation.removeEventListener('data_failed', handleError)
      animation.destroy()
      animationRef.current = null
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setIsLoadingMessageVisible(true), 1000)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (stopAnimation && animationRef.current && isAnimationReady && !stopHandledRef.current) {
      stopHandledRef.current = true
      const elapsed = Date.now() - mountedAtRef.current
      const delay = Math.max(0, minVisibleMs - elapsed)

      clearAllTimers()
      stopTimeoutRef.current = setTimeout(() => {
        animationRef.current?.stop()
        setHasPlayedOnce(true)
        finish(true)
      }, delay)
    }
  }, [stopAnimation, isAnimationReady, minVisibleMs])

  useEffect(() => {
    if (isCompleted && hasPlayedOnce && !canDismiss) finish(false)
  }, [isCompleted, hasPlayedOnce, canDismiss])

  useEffect(() => {
    if (canDismiss) onAnimationComplete?.(isForceDismiss)
  }, [canDismiss, isForceDismiss, onAnimationComplete])

  const handleDoubleClick = () => {
    animationRef.current?.stop()
    clearAllTimers()
    finish(true)
  }

  return (
    <div
      className="fixed left-0 top-0 z-[99] flex h-screen w-screen select-none justify-center bg-white"
      onDoubleClick={handleDoubleClick}
    >
      <div
        id="lottie"
        className="fixed bottom-[50vh] size-[150px] [transform:translateZ(0)] [will-change:transform,contents] max-sm:bottom-[42vh] max-sm:size-[120px]"
      />
      <div className="fixed bottom-[38vh] animate-[simple-fade-in_0.8s_ease-in] text-center font-approach-light text-[16px] leading-[28px] tracking-[0.2px] tall-lg:bottom-[42vh] tall-lg:text-[22px] tall-lg:leading-[34px] max-sm:bottom-[10vh] max-sm:leading-[34px]">
        <p className={cn('mx-0 my-[12px]', { hidden: !isLoadingMessageVisible })}>
          {t('loadingInProgress')}
        </p>
        {isMobile && window.innerHeight < 500 ? (
          <p className="mx-0 my-[12px]">{t('mobileNoticeToUseMonitor')}</p>
        ) : null}
        <div
          className={cn(
            "relative mt-[12px] h-[2px] w-full max-w-[80vw] bg-[#c9c9c9] before:absolute before:left-0 before:top-0 before:h-full before:w-0 before:bg-black before:content-['']",
            { 'before:animate-loading-complete': canDismiss },
          )}
        >
          <div className="absolute left-0 top-0 h-full w-0 animate-loading-progress bg-black" />
        </div>
      </div>
    </div>
  )
}

export default FullScreenLoading
